import { Container, rem, Stepper } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { createFileRoute, Outlet, useMatches } from '@tanstack/react-router';
import { useMemo } from 'react';

import { Success } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { useHeaderIsFixed } from '@apple/ui/shell/contexts/header';
import { useLeftSidebarCollapsed } from '@apple/ui/shell/contexts/left-sidebar';

type StepPath = '/cart/stores' | '/cart/products' | '/cart/quantities' | '/cart/submit';

export const Route = createFileRoute('/_authed/cart/_steps')({
	component: RouteComponent,
});

function RouteComponent() {
	const { auth } = Route.useRouteContext();
	const { isCollapsed } = useLeftSidebarCollapsed();
	const { isFixed } = useHeaderIsFixed();
	const navigate = Route.useNavigate();
	const matches = useMatches();
	const { t } = useTranslation('shop');
	const hideDescriptions = useMediaQuery('(max-width: 75em)');
	const skipQuantityStep = auth.profile?.singleStoreAccess === true;
	const stepPaths = useMemo<StepPath[]>(() => {
		const paths: StepPath[] = [];
		if (auth.canAccessMultipleStores) {
			paths.push('/cart/stores');
		}
		paths.push('/cart/products');
		if (!skipQuantityStep) {
			paths.push('/cart/quantities');
		}
		paths.push('/cart/submit');
		return paths;
	}, [auth.canAccessMultipleStores, skipQuantityStep]);
	const currentStepIndex = useMemo(() => {
		const lastMatch = matches.at(-1);
		const currentIndex = stepPaths.indexOf(lastMatch?.fullPath as StepPath);
		return Math.max(currentIndex, 0);
	}, [matches, stepPaths]);

	return (
		<Container
			fluid
			size='xl'
			mt={isCollapsed || isFixed ? 'var(--app-shell-header-height)' : 'xl'}
			px='xl'
			pt='md'
		>
			<Stepper
				size={hideDescriptions ? 'xs' : 'sm'}
				completedIcon={<Success c='white' />}
				active={currentStepIndex}
				onStepClick={step => {
					const path = stepPaths[step];
					if (path) {
						void navigate({ to: path });
					}
				}}
				allowNextStepsSelect={false}
				styles={{
					steps: {
						paddingLeft: !hideDescriptions ? rem(32) : undefined,
						paddingRight: !hideDescriptions ? rem(32) : undefined,
						paddingTop: rem(8),
					},
				}}
			>
				{auth.canAccessMultipleStores && (
					<Stepper.Step
						label={t('steps.stores.title')}
						description={!hideDescriptions && t('steps.stores.description')}
					>
						<Outlet />
					</Stepper.Step>
				)}

				<Stepper.Step
					label={t('steps.products.title')}
					description={!hideDescriptions && t('steps.products.description')}
				>
					<Outlet />
				</Stepper.Step>
				{!skipQuantityStep && (
					<Stepper.Step
						label={t('steps.quantities.title')}
						description={!hideDescriptions && t('steps.quantities.description')}
					>
						<Outlet />
					</Stepper.Step>
				)}
				<Stepper.Step
					label={t('steps.submit.title')}
					description={!hideDescriptions && t('steps.submit.description')}
				>
					<Outlet />
				</Stepper.Step>
				<Stepper.Completed>
					<Outlet />
				</Stepper.Completed>
			</Stepper>
		</Container>
	);
}
